import { Modal, spacing, Text } from '@scality/core-ui';
import { ChartTooltipContainer } from '@scality/core-ui/dist/components/charts/common/ChartTooltip';
import { Box } from '@scality/core-ui/dist/next';
import { zIndex } from '@scality/core-ui/dist/style/theme';
import { type ReactNode, useEffect, useState } from 'react';
import { createGlobalStyle } from 'styled-components';

/** Enlarged plot: 60% of the window, with a floor, as the RD-2364 design specifies. */
const VIEWPORT_SHARE = 0.6;
const MIN_EXPANDED_CHART_HEIGHT = 300;

const chartHeightForWindow = () => Math.max(MIN_EXPANDED_CHART_HEIGHT, Math.round(window.innerHeight * VIEWPORT_SHARE));

/**
 * core-ui portals its chart tooltip to a bare `<div>` on the body and gives it no stacking of its
 * own, so the modal's own z-index hides it. Raise it to the layer core-ui already reserves for
 * tooltips — global, because the element is created outside our tree. Only one chart modal is ever
 * open, so this mounts once.
 */
const RaiseChartTooltip = createGlobalStyle`
  ${ChartTooltipContainer} {
    z-index: ${zIndex.tooltip};
  }
`;

type ChartExpandModalProps = {
  title: string;
  /** A line above the plot — the window it covers, typically. */
  meta?: string;
  onClose: () => void;
  /** Draws the chart at the plot height the modal has room for. */
  children: (chartHeight: number) => ReactNode;
};

/**
 * A chart reopened full size over the page — what the enlarge button on a chart card does.
 *
 * It owns the geometry and nothing else: the card decides when a chart is enlarged, and the
 * widget decides how to lay itself out at the height handed to it.
 */
export const ChartExpandModal = ({ title, meta, onClose, children }: ChartExpandModalProps) => {
  const [chartHeight, setChartHeight] = useState(MIN_EXPANDED_CHART_HEIGHT);

  // 60vh is a window measurement, so it has to follow the window rather than be read once.
  useEffect(() => {
    const measure = () => setChartHeight(chartHeightForWindow());
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <Modal isOpen wide title={title} close={onClose} actions={{ confirm: { label: 'Close', onClick: onClose } }}>
      <RaiseChartTooltip />
      {/* The design's enlarged panel: as wide as the window allows, up to 1200px. */}
      <Box width="min(1200px, 88vw)" display="flex" flexDirection="column" gap={spacing.r8}>
        {meta && (
          <Text variant="Smaller" color="textSecondary">
            {meta}
          </Text>
        )}
        {children(chartHeight)}
      </Box>
    </Modal>
  );
};
